import {
  getPersonDescription,
  getPersonDetailLink,
} from '@/contact/person/utils';
import { DuplicatesCell } from '@/contact/person/view/duplicates-cell.component';
import { PersonEmail } from '@/contact/person/view/person-email.component';
import { UserStatusChip } from '@/contact/person/view/user-status-chip.component';
import { FilterPersonInput } from '@/graphql/generated/types';
import { Button } from '@/ui/button/button.component';
import { DropdownMenu } from '@/ui/dropdown-menu/dropdown-menu.component';
import { Link, RouteComponentProps } from '@reach/router';
import React, { ComponentType } from 'react';
import ReactDOM from 'react-dom';
import { getPaginationFromQuery } from 'shared/helpers/pagination.helper';
import { usePaginatedUrlQuery } from 'shared/hooks/url-query';
import { Translator } from 'shared/translator/translator.component';
import { Frame } from '../../../../frame';
import { DebouncedInput } from '../../../../ui/debounced-input/debounced-input.component';
import { ListView } from '../../../../ui/list-view/list-view.component';
import { Row } from '../../../../ui/table/row.component';
import { usePersonActions, useLinkAsDuplicate } from '../../actions/actions.hooks';
import { useCheckForDuplicatesMutation } from '../../actions/actions.generated';
import { error, success } from '@/ui/toaster/toast';
import { PersonListDataFragment, usePersonsQuery } from '../list.generated';
import modalStyles from '../../../../ui/modal/modal.module.scss';

export const CustomerList: ComponentType<RouteComponentProps> = (props) => {
  const [query] = usePaginatedUrlQuery<{ query: string }>();
  const { data, loading, refetch } = usePersonsQuery({
    variables: {
      pagination: getPaginationFromQuery(query),
      withDuplicates: true,
      filter: { query: query.query, userType: 'Customer' },
    },
    fetchPolicy: 'network-only',
  });

  const { modalAnonymize, modalDelete, askForAnonymize, askForDelete } =
    usePersonActions(() => refetch());

  const [checkForDuplicates, { loading: checkingDuplicates }] =
    useCheckForDuplicatesMutation();

  const handleCheckForDuplicates = async () => {
    try {
      await checkForDuplicates();
      success('Duplikat-Prüfung abgeschlossen', 'Die Liste wurde aktualisiert.');
      refetch();
    } catch (e: any) {
      error('Fehler bei der Duplikat-Prüfung', e.message);
    }
  };

  const { modalLinkDuplicate, askForLink } = useLinkAsDuplicate(refetch);

  const _ = Translator.useTranslator();

  const modalsEl = document.getElementById('modals');
  const blockingOverlay =
    checkingDuplicates && modalsEl
      ? ReactDOM.createPortal(
          <div className={modalStyles.dimmer}>
            <div className={modalStyles.blockingMessage}>
              <span className={modalStyles.spinner} />
              <p className={modalStyles.title}>Duplikat-Prüfung läuft…</p>
              <p className={modalStyles.subtitle}>
                Dies kann mehrere Minuten dauern. Bitte warte.
              </p>
            </div>
          </div>,
          modalsEl,
        )
      : null;

  return (
    <>
      {blockingOverlay}
      <Frame.SubTitle>Übersicht</Frame.SubTitle>
      <Frame.ActionBar>
        <Button linkTo="/contacts/customers/create" label="Neuer Kunde" />
        <Button
          label="Duplikate prüfen"
          onClick={handleCheckForDuplicates}
          loading={checkingDuplicates}
        />
      </Frame.ActionBar>
      <Frame.Content>
        {modalAnonymize}
        {modalDelete}
        {modalLinkDuplicate}
        <ListView<PersonListDataFragment, FilterPersonInput>
          loading={loading}
          data={data?.persons?.data || []}
          totalCount={data?.persons?.totalCount}
          columns={[
            {
              name: 'Name',
              sortable: true,
            },
            {
              name: 'Kundennummer',
              sortable: false,
            },
            {
              name: 'E-Mail',
              sortable: true,
            },
            {
              name: 'Benutzer',
              sortable: true,
            },
            {
              name: 'Unternehmen',
              sortable: true,
            },
            { name: 'Status', sortable: true },
            { name: 'Hinweise', sortable: false },
            { name: 'Aktionen', sortable: false },
          ]}
          renderFilter={({ value, onChange }) => (
            <DebouncedInput
              debounceRate={400}
              value={value?.query || ''}
              onChange={(value) => onChange('query', value)}
              placeholder={'Suche…'}
            />
          )}
          renderRow={(item) => {
            const detailLink = getPersonDetailLink(item);
            return (
              <Row key={item.id}>
                <td>
                  <Link to={detailLink}>{getPersonDescription(item)}</Link>
                </td>

                <td>
                  {(item.user as any)?.customerNumber}
                </td>

                <td>
                  <Link to={detailLink}>
                    <PersonEmail person={item} />
                  </Link>
                </td>
                <td>
                  <Link to={detailLink}>{item.user?.username}</Link>
                </td>

                <td>
                  {item.enterprises
                    .map((enterprise) => enterprise.name)
                    .join(', ')}
                </td>
                <td>
                  <UserStatusChip user={item.user} />
                </td>
                <td>
                  {item.__typename === 'PersonWithDuplicates' &&
                    item.duplicates && (
                      <DuplicatesCell
                        duplicates={item.duplicates}
                        linkTo={`../../${item.id}/duplicates`}
                      />
                    )}
                </td>
                <td>
                  <DropdownMenu
                    actions={[
                      {
                        label: 'Als Duplikat markieren',
                        onClick: () => askForLink(item.id),
                      },
                      {
                        label: 'Löschen (anonym)',
                        meaning: 'error',
                        onClick: () => askForAnonymize(item),
                      },
                      {
                        label: 'Löschen (destruktiv)',
                        meaning: 'error',
                        onClick: () => askForDelete(item),
                      },
                    ]}
                  />
                </td>
              </Row>
            );
          }}
        />
      </Frame.Content>
    </>
  );
};
